/** @format */

import { AsyncPipe, KeyValuePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, input } from '@angular/core';
import {
	GTGT_FILTER_NAME,
	GTGT_FILTER_VALUE_DSG_PL,
	GTGT_FILTER_VALUE_GG,
	GTGT_TITLE,
	shippingOptions
} from '@plp/data-access/constants';
import { HomefieldGlobalChipsModule, HomefieldTextLinkModule } from '@homefield';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconListFilterAlt
} from '@dsg-tech/homefield-angular-icons';
import {
	refreshProductList,
	removeAllFilters,
	selectProductListCategoryDetails,
	selectProductListFacetVOs,
	selectSelectedFilters,
	setFiltering
} from '@plp/data-access/state';
import { selectUserStoreInfo, selectUserZipCode } from '@pdp/data-access/state';

import { Chain } from '@shared/data-access/interfaces';
import { FacetVO } from '@plp/data-access/interfaces';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { ProductListUIService } from '@plp/data-access/services';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'plp-components-filter-chips',
	imports: [
		AsyncPipe,
		KeyValuePipe,
		NgClass,
		HomefieldGlobalChipsModule,
		HomefieldIconLibraryModule,
		HomefieldTextLinkModule,
		HmfActionTextComponent
	],
	templateUrl: './filter-chips.component.html',
	styleUrl: './filter-chips.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterChipsComponent implements OnInit, OnDestroy {
	@Input() wrapChips = false;
	sortPositionChangeEnabled = input(false);
	sameDayDeliveryEnabled = input(false);

	private readonly UNSUBSCRIBE$ = new Subject<void>();

	readonly categoryDetails$ = this.store
		.select(selectProductListCategoryDetails)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedStoreInfo$ = this.store
		.select(selectUserStoreInfo)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectUserZipCode$ = this.store
		.select(selectUserZipCode)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListFacetVOs$ = this.store
		.select(selectProductListFacetVOs)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly selectedFilters$ = this.store
		.select(selectSelectedFilters)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	GTGT_TITLE = GTGT_TITLE;
	GTGT_FILTER_NAME = GTGT_FILTER_NAME;

	filterDisplayTextMap = {};
	filterLabelLookUp = {};
	isSelectedFilterEmpty = false;
	mobileFilterChip = false;
	selectedFilters: Record<string, string[]> = {};
	shippingOptions = shippingOptions;
	chainAbbr = 'dsg';

	constructor(
		private readonly chain: Chain,
		private readonly store: Store,
		private readonly productListUIService: ProductListUIService,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.chainAbbr = this.chain.chainIdentifierAbbr;
		this.homefieldIconsLibraryService.registerIcons([homefieldIconListFilterAlt]);
	}

	ngOnInit(): void {
		this.filterDisplayTextMap = { ...shippingOptions };

		this.productListFacetVOs$.subscribe((facetOptions: FacetVO[]) => {
			if (!facetOptions || facetOptions.length === 0) {
				return;
			}

			facetOptions.forEach((facet: FacetVO) => {
				if (facet.attrIdentifier !== GTGT_FILTER_NAME) {
					this.filterLabelLookUp[facet.attrIdentifier] = `${facet.attrName}: `;
				}
			});
		});

		this.selectedFilters$.subscribe((filterOption: Record<string, string[]>) => {
			this.selectedFilters = filterOption;
			this.isSelectedFilterEmpty = Object.keys(this.selectedFilters).length === 0;
			// format prices
			if (
				this.selectedFilters['dickssportinggoodsofferprice'] &&
				this.selectedFilters['dickssportinggoodsofferprice'].length > 0
			) {
				this.selectedFilters['dickssportinggoodsofferprice'].forEach((priceRange) => {
					this.filterDisplayTextMap[priceRange] = {
						label: this.productListUIService.addSymbolForPriceDisplay(priceRange)
					};
				});
			}

			if (
				this.selectedFilters[GTGT_FILTER_NAME] &&
				this.selectedFilters[GTGT_FILTER_NAME].length > 0
			) {
				this.filterDisplayTextMap['Y'] =
					this.chainAbbr === 'gg'
						? { label: GTGT_FILTER_VALUE_GG }
						: { label: GTGT_FILTER_VALUE_DSG_PL };
			}
		});
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	removeFilter(filterType: string, filterLabel: string): void {
		const updatedFilters = this.selectedFilters[filterType].filter(
			(selectedFilter) => selectedFilter !== filterLabel
		);

		this.store.dispatch(
			setFiltering({
				selectedFilters: {
					[filterType]: [...updatedFilters]
				}
			})
		);
		this.store.dispatch(refreshProductList());
	}

	removeFilters(): void {
		this.store.dispatch(removeAllFilters());
		this.store.dispatch(refreshProductList());
	}

	trackByKey(index: number, item: any): string {
		return index.toString() + item.key;
	}

	trackByLabel(index: number, item: any): string {
		return index.toString() + item;
	}

	trackByFn(index: number, item: any): string {
		return item.key + index;
	}

	trackByFilter(index: number, item: any): string {
		return item + index;
	}
}
